import React, { useEffect } from "react";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string } from "yup";
import {
  getAllPartName,
  addPartyNominationType,
  updatePartyNominationType,
  getAllPartyNominationType,
} from "src/app-redux/configuration/actions/configurationActions";
import {
  loader,
  selectAllPartyNameRowsList,
  selectAllPartyNominationsNameRowsList,
} from "src/app-redux/configuration/configurationSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddPartyNominationType, IAddPaymentType } from "src/common/typings/configuration";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import { Prompt } from "react-router-dom";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonLoader from "src/components/Loader/CommonLoader";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
  left0: { paddingLeft: "0px!important" },
  right0: { paddingRight: "0px!important" },
  left20: { paddingLeft: "20px!important" },
  normal: { paddingLeft: "8px!important", paddingRight: "8px!important" },
}));

export interface IForm extends IAddPaymentType {}

interface Props {}

export function InputFields({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [isEditMode, setisEditMode] = React.useState<boolean>(false);
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const loading = useAppSelector(loader);
  const allPartyNameRows = useAppSelector(selectAllPartyNameRowsList);
  const allPartyNominationsRows = useAppSelector(selectAllPartyNominationsNameRowsList);

  // Form Submission
  const validationSchema = object({
    name: string().required("This field is required").max(50, "Maximum 50 digits are allowed."),
  });

  const formik = useFormik<IAddPartyNominationType>({
    enableReinitialize: true,
    initialValues: {
      id: "",
      name: "",
      isActive: false,
      PartyId: "",
      PerCbm: "",
      PerIndex: "",
      fuelCharges: "",
      trackingCharges: "",
      documentationCharges: "",
      terminalHandlingCharges: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      let res;
      if (isEditMode) res = await dispatch(updatePartyNominationType(values));
      else res = await dispatch(addPartyNominationType(values));
      if (res?.payload) {
        dispatch(getAllPartyNominationType(false));
        actions.setSubmitting(false);
        actions.resetForm();
        setisEditMode(false);
        setIsDirty(false);
      }
    },
  });

  // End Form Submission
  const handleUpdateClick = (params: any) => {
    setisEditMode(true);
    formik.setFieldValue("id", params.id);
    formik.setFieldValue("name", params.name);
    formik.setFieldValue("isActive", params.isActive);
    formik.setFieldValue("PartyId", params.partyId);
    formik.setFieldValue("PerCbm", params.perCbm);
    formik.setFieldValue("PerIndex", params.perIndex);
    formik.setFieldValue("fuelCharges", params.fuelCharges);
    formik.setFieldValue("trackingCharges", params.trackingCharges);
    formik.setFieldValue("documentationCharges", params.documentationCharges);
    formik.setFieldValue("terminalHandlingCharges", params.terminalHandlingCharges);
  };

  function handleChange(e: any) {
    formik.handleChange(e);
    e.target.value || JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
      ? setIsDirty(true)
      : setIsDirty(false);
  }

  useEffect(() => {
    dispatch(getAllPartName(true));
    dispatch(getAllPartyNominationType(false));
  }, []);

  useEffect(() => {
    if (allPartyNameRows.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirty(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [allPartyNameRows]);

  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);

  return (
    <>
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={12} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        label="Party Name"
                        placeholder="Select Party Name"
                        name="PartyId"
                        optionsType={allPartyNameRows}
                        getOptionLabel={(option) => option.name}
                        required
                        value={
                          allPartyNameRows.find((st: any) => st.id === formik.values.PartyId) ||
                          null
                        }
                        onChange={(option: any) => {
                          formik.setFieldValue("PartyId", option.id);
                          handleChange({ target: { value: option.id } });
                        }}
                        error={formik.touched.PartyId && Boolean(formik.errors.PartyId)}
                        helperText={formik.touched.PartyId && formik.errors.PartyId}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="string"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Nomination Name"
                        name="name"
                        placeholder="Enter Nomination Name"
                        value={formik.values.name}
                        onChange={handleChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="string"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Per CBM"
                        name="PerCbm"
                        placeholder="Per CBM Amount"
                        disabled={false}
                        value={formik.values.PerCbm}
                        onChange={handleChange}
                        error={formik.touched.PerCbm && Boolean(formik.errors.PerCbm)}
                        helperText={formik.touched.PerCbm && formik.errors.PerCbm}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="string"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Per Index"
                        name="PerIndex"
                        placeholder="Per Index Amount"
                        disabled={false}
                        value={formik.values.PerIndex}
                        onChange={handleChange}
                        error={formik.touched.PerIndex && Boolean(formik.errors.PerIndex)}
                        helperText={formik.touched.PerIndex && formik.errors.PerIndex}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="number"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Fuel Charges"
                        name="fuelCharges"
                        placeholder="Enter Fuel Charges"
                        disabled={false}
                        value={formik.values.fuelCharges}
                        onChange={handleChange}
                        // onChange={(e) =>
                        //   formik.setFieldValue("fuelCharges", Number(e.target.value))
                        // }
                        error={formik.touched.fuelCharges && Boolean(formik.errors.fuelCharges)}
                        helperText={formik.touched.fuelCharges && formik.errors.fuelCharges}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="number"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Tracking Charges"
                        name="trackingCharges"
                        placeholder="Enter Tracking Charges"
                        disabled={false}
                        value={formik.values.trackingCharges}
                        // onChange={handleChange}
                        onChange={(e) =>
                          formik.setFieldValue("trackingCharges", Number(e.target.value))
                        }
                        error={
                          formik.touched.trackingCharges && Boolean(formik.errors.trackingCharges)
                        }
                        helperText={formik.touched.trackingCharges && formik.errors.trackingCharges}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="number"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Documentation Charges Per Index"
                        name="documentationCharges "
                        placeholder="Enter Documentation Charges Per Index"
                        disabled={false}
                        value={formik.values.documentationCharges}
                        // onChange={handleChange}
                        onChange={(e) =>
                          formik.setFieldValue("documentationCharges", Number(e.target.value))
                        }
                        error={
                          formik.touched.documentationCharges &&
                          Boolean(formik.errors.documentationCharges)
                        }
                        helperText={
                          formik.touched.documentationCharges && formik.errors.documentationCharges
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="number"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Terminal Handling Charges Per CBM"
                        name="terminalHandlingCharges"
                        placeholder="Enter Terminal Handling Charges Per CBM"
                        disabled={false}
                        value={formik.values.terminalHandlingCharges}
                        // onChange={handleChange}
                        onChange={(e) =>
                          formik.setFieldValue("terminalHandlingCharges", Number(e.target.value))
                        }
                        error={
                          formik.touched.terminalHandlingCharges &&
                          Boolean(formik.errors.terminalHandlingCharges)
                        }
                        helperText={
                          formik.touched.terminalHandlingCharges &&
                          formik.errors.terminalHandlingCharges
                        }
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={6} md={2} lg={2} style={{ paddingRight: 0 }}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2} lg={6} style={{ paddingRight: 0 }}>
                    <Box mb={1} mt={1} className={"headerButtonsConfig"} style={{ float: "right" }}>
                      <CommonButton
                        // isGreen
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          setisEditMode(false);
                          setIsDirty(false);
                          formik.resetForm();
                        }}
                      >
                        Reset
                      </CommonButton>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          formik.submitForm();
                        }}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <CommonMaterialGrid
                      defaultPerPageRecord={10}
                      height={400}
                      rows={allPartyNominationsRows || []}
                      columns={displayColumns}
                    />
                  </Grid>
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* End Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
